import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { createEmptyNote, type NoteRecord } from '@/types/note';

interface NoteRow {
  id: string;
  user_id: string;
  exam_name: string | null;
  exam_type: string | null;
  subject: string | null;
  problem_number: string | null;
  section: string | null;
  problem_text: string | null;
  problem_image: string | null;
  chapter: string | null;
  problem_type: string | null;
  wrong_reason: string | null;
  key_point: string | null;
  retry_solution: string | null;
  created_at: string | null;
}

function rowToNote(row: NoteRow): NoteRecord {
  return {
    ...createEmptyNote(),
    id: row.id,
    examName: row.exam_name ?? '',
    examType: row.exam_type ?? '수능',
    subject: row.subject ?? '수학',
    problemNumber: row.problem_number ?? '',
    section: row.section ?? '',
    problemText: row.problem_text ?? '',
    problemImage: row.problem_image ?? undefined,
    chapter: row.chapter ?? '',
    problemType: row.problem_type ?? '',
    wrongReason: row.wrong_reason ?? '',
    keyPoint: row.key_point ?? '',
    retrySolution: row.retry_solution ?? '',
    createdAt: row.created_at ?? new Date().toISOString(),
  };
}

function makeId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function noteToRow(note: NoteRecord, userId: string): NoteRow {
  return {
    id: note.id,
    user_id: userId,
    exam_name: note.examName,
    exam_type: note.examType,
    subject: note.subject,
    problem_number: note.problemNumber,
    section: note.section,
    problem_text: note.problemText,
    problem_image: note.problemImage ?? null,
    chapter: note.chapter,
    problem_type: note.problemType,
    wrong_reason: note.wrongReason,
    key_point: note.keyPoint,
    retry_solution: note.retrySolution,
    created_at: note.createdAt,
  };
}

/**
 * 로그인한 본인의 오답노트만 조회/저장합니다. (user_id 기준)
 */
export function useNotes(userId: string | null) {
  const [notes, setNotes] = useState<NoteRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const load = useCallback(async () => {
    if (!userId) {
      setNotes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('notes')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });
      if (err) throw err;
      setNotes(((data ?? []) as NoteRow[]).map(rowToNote));
    } catch {
      setError('오답노트를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(
    () => () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current.clear();
    },
    [],
  );

  const persist = useCallback(
    (note: NoteRecord) => {
      if (!userId) return;
      const current = timers.current.get(note.id);
      if (current) clearTimeout(current);
      const timer = setTimeout(() => {
        timers.current.delete(note.id);
        void supabase
          .from('notes')
          .upsert(noteToRow(note, userId))
          .then(({ error: err }) => {
            if (err) setError('오답노트 저장에 실패했어요. 잠시 후 다시 시도해 주세요.');
          });
      }, 600);
      timers.current.set(note.id, timer);
    },
    [userId],
  );

  const saveNote = useCallback(
    (note: NoteRecord): NoteRecord => {
      const saved: NoteRecord = {
        ...note,
        id: note.id || makeId(),
        createdAt: note.createdAt || new Date().toISOString(),
      };
      setNotes((prev) => {
        const exists = prev.some((n) => n.id === saved.id);
        return exists ? prev.map((n) => (n.id === saved.id ? saved : n)) : [...prev, saved];
      });
      persist(saved);
      return saved;
    },
    [persist],
  );

  const deleteNote = useCallback(
    (id: string) => {
      const current = timers.current.get(id);
      if (current) {
        clearTimeout(current);
        timers.current.delete(id);
      }
      setNotes((prev) => prev.filter((n) => n.id !== id));
      if (!userId) return;
      void supabase
        .from('notes')
        .delete()
        .eq('id', id)
        .eq('user_id', userId)
        .then(({ error: err }) => {
          if (err) setError('삭제에 실패했어요. 잠시 후 다시 시도해 주세요.');
        });
    },
    [userId],
  );

  const clearNotes = useCallback(() => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current.clear();
    setNotes([]);
    if (!userId) return;
    void supabase
      .from('notes')
      .delete()
      .eq('user_id', userId)
      .then(({ error: err }) => {
        if (err) setError('삭제에 실패했어요. 잠시 후 다시 시도해 주세요.');
      });
  }, [userId]);

  return { notes, saveNote, deleteNote, clearNotes, loading, error, refresh: load };
}